import React, { createContext, useContext, type ReactNode } from 'react';

import type { RegisterSection, SectionId } from '../model/operations';
import type { RowActionId, SectionActionId } from '../viewmodels/rowTypes';

export type SectionCommands = {
  selectEntry: (sectionId: string, entryId: SectionActionId) => void;
  retry: (sectionId: string) => void;
  toggle: (sectionId: string) => void;
  register: RegisterSection;
  reorder: (activeId: string, overId: string) => void;
};

export type QuestionCommands = {
  selectEntry: (questionId: string, entryId: RowActionId) => void;
  retry: (questionId: string) => void;
  reorder: (sectionId: string, activeId: string, overId: string) => void;
};

export type NavigationCommands = {
  selectSection: (sectionId: SectionId) => void;
  refresh: () => void;
};

export type CustomizationCommands = {
  section: SectionCommands;
  question: QuestionCommands;
  navigation: NavigationCommands;
};

const CustomizationCommandsContext =
  createContext<CustomizationCommands | null>(null);

type ProviderProps = {
  commands: CustomizationCommands;
  children: ReactNode;
};

export function CustomizationCommandsProvider({
  commands,
  children,
}: ProviderProps) {
  return (
    <CustomizationCommandsContext.Provider value={commands}>
      {children}
    </CustomizationCommandsContext.Provider>
  );
}

function useCommands(hookName: string): CustomizationCommands {
  const commands = useContext(CustomizationCommandsContext);

  if (!commands) {
    throw new Error(
      `${hookName} must be used within CustomizationCommandsProvider`
    );
  }

  return commands;
}

export function useSectionCommands(): SectionCommands {
  return useCommands('useSectionCommands').section;
}

export function useQuestionCommands(): QuestionCommands {
  return useCommands('useQuestionCommands').question;
}

export function useNavigationCommands(): NavigationCommands {
  return useCommands('useNavigationCommands').navigation;
}
